// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useState } from 'react';

/**
 * One course's per-hole to-do list, as geodata works it out: which of tee, fairway and green
 * each hole shows players and from whom, what it is missing, and what the latest decision's
 * checks held back or flagged. Every number and verdict is geodata's; the page only shows them.
 */

export type HoleCheckFinding = {
  rule: string;
  kind: string;
  featureId: string | null;
  source: string | null;
  confidenceBefore: number | null;
  confidenceAfter: number | null;
  /** The share of the row's area a trim took off; null when nothing was trimmed. */
  removedShare: number | null;
  /** True when the check held a satellite row back; false for a flag left for a person. */
  held: boolean;
};

export type HoleCheckHole = {
  holeNumber: number;
  par: number | null;
  /** Sources behind what players see, strongest first; empty when the hole shows none. */
  tee: string[];
  fairway: string[];
  green: string[];
  missing: string[];
  findings: HoleCheckFinding[];
};

export type HoleChecks = {
  /** Null until a decision has run the checks on this course. */
  checkedAt: string | null;
  held: number;
  flagged: number;
  incomplete: number;
  holes: HoleCheckHole[];
};

type Row = Record<string, unknown>;

const num = (row: Row, key: string): number => {
  const value = row[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
};

const numOrNull = (row: Row, key: string): number | null => {
  const value = row[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const str = (row: Row, key: string): string | null => {
  const value = row[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
};

const strings = (row: Row, key: string): string[] => {
  const value = row[key];
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
};

const rows = (row: Row, key: string): Row[] => {
  const value = row[key];
  return Array.isArray(value) ? value.filter((v): v is Row => typeof v === 'object' && v !== null) : [];
};

/** Maps the BFF's `/api/geodata/courses/{id}/hole-checks` body. Exported for direct unit testing. */
export const mapHoleChecks = (body: unknown): HoleChecks | null => {
  if (typeof body !== 'object' || body === null) return null;
  const row = body as Row;
  return {
    checkedAt: str(row, 'checkedAt'),
    held: num(row, 'held'),
    flagged: num(row, 'flagged'),
    incomplete: num(row, 'incomplete'),
    holes: rows(row, 'holes')
      .map((hole) => ({
        holeNumber: num(hole, 'holeNumber'),
        par: numOrNull(hole, 'par'),
        tee: strings(hole, 'tee'),
        fairway: strings(hole, 'fairway'),
        green: strings(hole, 'green'),
        missing: strings(hole, 'missing'),
        findings: rows(hole, 'findings').map((finding) => ({
          rule: str(finding, 'rule') ?? '',
          kind: str(finding, 'kind') ?? '',
          featureId: str(finding, 'featureId'),
          source: str(finding, 'source'),
          confidenceBefore: numOrNull(finding, 'confidenceBefore'),
          confidenceAfter: numOrNull(finding, 'confidenceAfter'),
          removedShare: numOrNull(finding, 'removedShare'),
          held: finding.held === true,
        })),
      }))
      .filter((hole) => hole.holeNumber > 0),
  };
};

export type HoleChecksState = {
  report: HoleChecks | null;
  loading: boolean;
  error: 'forbidden' | 'unavailable' | 'failed' | null;
};

export function useHoleChecks(
  apiBaseUrl: string,
  token: string | null | undefined,
  courseId: string | null
): HoleChecksState {
  const [report, setReport] = useState<HoleChecks | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<HoleChecksState['error']>(null);

  useEffect(() => {
    if (!token || !courseId) {
      setReport(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/geodata/courses/${encodeURIComponent(courseId)}/hole-checks`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        if (!response.ok) {
          setReport(null);
          setError(response.status === 403 ? 'forbidden' : response.status === 404 ? 'unavailable' : 'failed');
          return;
        }
        const body = mapHoleChecks(await response.json());
        if (!cancelled) setReport(body);
      } catch {
        if (!cancelled) {
          setReport(null);
          setError('failed');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, courseId, token]);

  return { report, loading, error };
}
